// src/components/admin/page-sections/StatisticsSection.js
import React, { useEffect, useRef } from 'react';

const StatisticsSection = ({ content }) => {
  const {
    statistics = [
      { value: "1000+", label: "Happy Clients", delay: 0 },
      { value: "50K+", label: "Products Delivered", delay: 200 },
      { value: "98%", label: "Client Satisfaction", delay: 400 },
      { value: "24/7", label: "Customer Support", delay: 600 }
    ],
    backgroundColor = '#033568',
    statsPerRow = {
      mobile: 1,
      tablet: 2,
      desktop: 4
    },
    textColor = '#ffffff',
    statValueSize = 'text-4xl md:text-5xl',
    statLabelSize = 'text-lg',
    statLabelColor = '#d1d5db', // text-gray-300
    backgroundOverlayEnabled = true,
    overlayFrom = 'rgba(31, 41, 55, 0.3)', // from-gray-800/30
    overlayTo = 'rgba(30, 58, 138, 0.3)', // to-blue-800/30
    showFloatingOrbs = true,
    orbColors = ['#FFEB3B', '#4299E1'], // yellow-500, blue-500
    cardBackgroundColor = 'rgba(255, 255, 255, 0.05)', // bg-white/5
    cardBorderColor = 'rgba(255, 255, 255, 0.1)', // border-white/10
    cardHoverEffect = true,
    showSeparatorLines = true,
    separatorColorTop = 'rgba(1,50,99,0.8)',
    separatorColorBottom = 'rgba(1,50,99,0.8)',
    paddingTop = '8rem',
    paddingBottom = '10rem',
    marginBottom = '5rem',
    animationEnabled = true,
    animationStyle = 'fade', // 'fade', 'scale', or 'slide'
    shadowEnabled = true,
    borderRadius = '0.5rem',
    cardGap = '2rem',
    containerMaxWidth = 'max-w-7xl'
  } = content;

  // Reference the container for intersection observer
  const containerRef = useRef(null);

  // Setup intersection observer for animations
  useEffect(() => {
    if (!animationEnabled) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const children = entry.target.children;
          Array.from(children).forEach((child, index) => {
            const statIndex = Array.from(child.parentNode.children).indexOf(child);
            const delay = statistics[statIndex]?.delay || index * 200;
            
            setTimeout(() => {
              child.style.opacity = '1';
              child.style.transform = 'translateY(0)';
            }, delay);
          });
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );
    
    if (containerRef.current) {
      // Set initial styles
      Array.from(containerRef.current.children).forEach(child => {
        child.style.opacity = '0';
        
        if (animationStyle === 'slide') {
          child.style.transform = 'translateY(20px)';
        } else if (animationStyle === 'scale') {
          child.style.transform = 'scale(0.9)';
        } else {
          child.style.transform = 'translateY(20px)'; // Default to fade + slide
        }
        
        child.style.transition = 'opacity 0.6s ease-out, transform 0.6s ease-out';
      });
      
      observer.observe(containerRef.current);
    }
    
    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [animationEnabled, animationStyle, statistics]);

  return (
    <div 
      className="py-20 relative overflow-hidden shadow-xl" 
      style={{ 
        backgroundColor, 
        paddingTop, 
        paddingBottom, 
        marginBottom 
      }}
    >
      {backgroundOverlayEnabled && (
        <div 
          className="absolute inset-0" 
          style={{ 
            backgroundImage: `linear-gradient(to bottom right, ${overlayFrom}, ${overlayTo})` 
          }}
        ></div>
      )}
      
      {showFloatingOrbs && (
        <>
          <div
            className="absolute -top-40 -right-40 w-80 h-80 rounded-full blur-3xl opacity-20"
            style={{ backgroundColor: orbColors[0] }}
          ></div>
          <div
            className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full blur-3xl opacity-20"
            style={{ backgroundColor: orbColors[1] }}
          ></div>
        </>
      )}
      
      {showSeparatorLines && (
        <>
          <div 
            className="absolute top-0 left-0 right-0 h-1/3"
            style={{
              background: `linear-gradient(to bottom, ${separatorColorTop} 0%, rgba(1,50,99,0) 100%)`
            }}
          ></div>
          <div 
            className="absolute bottom-0 left-0 right-0 h-1/3"
            style={{
              background: `linear-gradient(to top, ${separatorColorBottom} 0%, rgba(1,50,99,0) 100%)`
            }}
          ></div>
        </>
      )}
      
      <div className={`container mx-auto px-4 relative z-10 ${containerMaxWidth}`}>
        <div 
          ref={containerRef}
          className={`grid grid-cols-${statsPerRow.mobile} sm:grid-cols-${statsPerRow.tablet} md:grid-cols-${statsPerRow.desktop} gap-8`}
          style={{ gap: cardGap }}
        >
          {statistics.map((stat, index) => (
            <div 
              key={index}
              className={`p-6 rounded-lg border backdrop-blur-sm ${shadowEnabled ? 'shadow-lg' : ''} ${cardHoverEffect ? 'transform hover:scale-105 transition-all duration-300' : ''}`}
              style={{
                backgroundColor: cardBackgroundColor,
                borderColor: cardBorderColor,
                color: textColor,
                borderRadius
              }}
            >
              <div className={`font-bold mb-2 ${statValueSize}`}>{stat.value}</div>
              <div className={`${statLabelSize}`} style={{ color: statLabelColor }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StatisticsSection;